import {
	Box,
	Button,
	Icons,
	Inline,
	Select,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { formatDate, parseDate } from "@sembark-travel/datetime-utils"
import {
	FieldArray,
	GetFieldValue,
	SelectField,
	SelectInputField,
	TextInputField,
	useFieldValue,
	useForm,
} from "@sembark-travel/ui-form"
import {
	type TAutomationCheckpointFormValue,
	type TAutomationEveryFormValue,
	type TAutomationOffsetFormValue,
	createCheckpointFormValue,
	createEveryFormValue,
	createOffsetFormValue,
	everyToString,
	offsetToString,
} from "./store"

/**
 * The shared checkpoints editor: an add/remove list of "N units before/after"
 * rows bound to a form array field (`name`). Used by the advanced automation
 * builder and the template-definition form. `previewLabel` (e.g. "the due
 * date") is the basis the rows describe; omit it to hide the per-row preview.
 */
export function CheckpointsField({
	name,
	previewLabel,
	minOne = true,
}: {
	name: string
	previewLabel?: string
	minOne?: boolean
}) {
	return (
		<Stack gap="4">
			<FieldArray<TAutomationCheckpointFormValue> name={name}>
				{({ fields }) => (
					<Stack gap="3">
						{fields.map((name, index) => (
							<Box key={name}>
								<Stack gap="1">
									<Inline gap="2" alignItems="center">
										<Inline
											alignItems="center"
											justifyContent="center"
											fontWeight="semibold"
											size="8"
											bgColor="subtle"
											borderWidth="1"
											rounded="full"
										>
											{index + 1}
										</Inline>
										<Inline>
											<Box>
												<SelectInputField name={`${name}.direction`} size="sm">
													<option value="before">Before</option>
													<option value="after">After</option>
												</SelectInputField>
											</Box>
											<Box style={{ maxWidth: "100px" }}>
												<TextInputField
													name={`${name}.amount`}
													type="number"
													size="sm"
												/>
											</Box>
											<Box>
												<SelectInputField name={`${name}.unit`} size="sm">
													<option value="minutes">Minutes</option>
													<option value="hours">Hours</option>
													<option value="days">Days</option>
												</SelectInputField>
											</Box>
										</Inline>
										<Button
											type="button"
											level="tertiary"
											disabled={minOne && fields.length === 1}
											onClick={() => fields.remove(index)}
											size="sm"
										>
											<Icons.Cancel />
										</Button>
									</Inline>
									{/* Said on the row itself: the send window is where
									    someone would look for this rule, but the row is
									    where they choose the unit that escapes it. */}
									<GetFieldValue<TAutomationCheckpointFormValue["unit"]>
										name={`${name}.unit`}
									>
										{({ value: unit }) =>
											unit === "minutes" || unit === "hours" ? (
												<Box paddingLeft="10">
													<Text fontSize="sm" color="warning">
														Sent at exactly this time, even outside the send
														window — shifting it would change what it means.
													</Text>
												</Box>
											) : null
										}
									</GetFieldValue>
								</Stack>
							</Box>
						))}

						<Box>
							<Button
								type="button"
								level="secondary"
								size="sm"
								onClick={() => {
									fields.push(createCheckpointFormValue())
								}}
							>
								<Icons.Plus /> Add {!fields.length ? "Checkpoint" : "More"}
							</Button>
						</Box>
					</Stack>
				)}
			</FieldArray>

			{previewLabel ? (
				<GetFieldValue<TAutomationCheckpointFormValue[]> name={name}>
					{({ value: checkpoints }) =>
						!(checkpoints || []).length ? null : (
							<Text color="muted" fontSize="sm" as="blockquote">
								Runs{" "}
								{checkpoints
									.map(
										(checkpoint) =>
											`${checkpoint.amount || "?"} ${checkpoint.unit} ${checkpoint.direction}`
									)
									.join(", ")}{" "}
								{previewLabel}.
							</Text>
						)
					}
				</GetFieldValue>
			) : null}
		</Stack>
	)
}

// A signed offset editor (direction · amount · unit) bound to `${name}.direction`
// etc. Shared by the repeat window's "from" / "until".
function OffsetInput({ name }: { name: string }) {
	return (
		<Inline>
			<SelectInputField name={`${name}.direction`}>
				<option value="before">Before</option>
				<option value="after">After</option>
			</SelectInputField>
			<Box style={{ maxWidth: "100px" }}>
				<TextInputField name={`${name}.amount`} type="number" />
			</Box>
			<SelectInputField name={`${name}.unit`}>
				<option value="minutes">Minutes</option>
				<option value="hours">Hours</option>
				<option value="days">Days</option>
			</SelectInputField>
		</Inline>
	)
}

function describeOffset(
	offset: TAutomationOffsetFormValue,
	basis: string
): string {
	const amount =
		(typeof offset.amount === "string"
			? offset.amount.trim()
			: String(offset.amount ?? "").trim()) || "?"

	if (Number(amount) === 0) {
		return basis
	}

	return `${amount} ${offset.unit} ${offset.direction === "before" ? "before" : "after"} ${basis}`
}

function describeIntervalWindow(
	start: TAutomationOffsetFormValue | undefined,
	end: TAutomationOffsetFormValue | undefined,
	every: TAutomationEveryFormValue | undefined,
	basis: string
): string {
	const everyValue = everyToString(every)

	if (
		!start ||
		!end ||
		!offsetToString(start) ||
		!offsetToString(end) ||
		!everyValue
	) {
		return "Complete the repeat window to preview how the automation will schedule runs."
	}

	return `Runs every ${everyValue} from ${describeOffset(start, basis)} until ${describeOffset(end, basis)}.`
}

/**
 * The shared "repeat window" editor: an optional window that fires repeated runs
 * every `interval_every` between `interval_start_offset` and `interval_end_offset`
 * (both relative to the schedule field). `previewLabel` is the basis shown in the
 * preview; omit it to hide the preview. Bound to the fixed `enable_interval_window`
 * / `interval_*` form fields.
 */
export function IntervalWindowField({
	previewLabel,
}: {
	previewLabel?: string
}) {
	const form = useForm()

	const { value: enabled } = useFieldValue<boolean>("enable_interval_window")

	return (
		<Stack gap="3">
			{!enabled ? null : (
				<Stack gap="1">
					<Inline gap="4" flexWrap="wrap" collapseBelow="sm">
						<Box>
							<Stack gap="1">
								<Text fontWeight="semibold" fontSize="sm">
									Repeat From
									{previewLabel ? (
										<Text as="span">&nbsp;"{previewLabel}"</Text>
									) : null}
								</Text>
								<OffsetInput name="interval_start_offset" />
							</Stack>
						</Box>
						<Box>
							<Stack gap="1">
								<Text fontWeight="semibold" fontSize="sm">
									Repeat Until
								</Text>
								<OffsetInput name="interval_end_offset" />
							</Stack>
						</Box>
						<Box>
							<Stack gap="1">
								<Text fontWeight="semibold" fontSize="sm">
									Repeat Every
								</Text>
								<Inline>
									<Box
										style={{
											maxWidth: "100px",
										}}
									>
										<TextInputField
											name="interval_every.amount"
											type="number"
										/>
									</Box>
									<SelectInputField name="interval_every.unit">
										<option value="minutes">Minutes</option>
										<option value="hours">Hours</option>
										<option value="days">Days</option>
									</SelectInputField>
								</Inline>
							</Stack>
						</Box>
					</Inline>
					{previewLabel ? (
						<GetFieldValue<TAutomationOffsetFormValue> name="interval_start_offset">
							{({ value: startOffset }) => (
								<GetFieldValue<TAutomationOffsetFormValue> name="interval_end_offset">
									{({ value: endOffset }) => (
										<GetFieldValue<TAutomationEveryFormValue> name="interval_every">
											{({ value: every }) => (
												<Text color="muted" fontSize="sm">
													{describeIntervalWindow(
														startOffset,
														endOffset,
														every,
														previewLabel
													)}
												</Text>
											)}
										</GetFieldValue>
									)}
								</GetFieldValue>
							)}
						</GetFieldValue>
					) : null}
				</Stack>
			)}
			<Box>
				<Button
					type="button"
					level="secondary"
					size="sm"
					onClick={() => {
						if (!enabled) {
							// Seed sensible defaults on first enable (a no-op when the
							// form already carries them).
							const values = form.getState().values as Record<string, unknown>
							if (!values.interval_start_offset) {
								form.change(
									"interval_start_offset",
									createOffsetFormValue({ amount: "0", unit: "hours" })
								)
							}
							if (!values.interval_end_offset) {
								form.change(
									"interval_end_offset",
									createOffsetFormValue({ amount: "3", unit: "days" })
								)
							}
							if (!values.interval_every) {
								form.change("interval_every", createEveryFormValue())
							}
						}
						form.change("enable_interval_window", !enabled)
					}}
				>
					{enabled ? "Remove Repeat Window" : "Add Repeat Window"}
				</Button>
			</Box>
		</Stack>
	)
}

function parseClockTime(query: string): Date | undefined {
	try {
		return parseDate(query, "HH:mm")
	} catch {
		return undefined
	}
}

// A creatable time-of-day picker storing an "HH:mm" string, matching the
// pattern used elsewhere (e.g. cab pickup time).
function TimeSelectField({ name }: { name: string }) {
	return (
		<Box style={{ maxWidth: "100px" }}>
			<SelectField
				select={Select}
				name={name}
				size="sm"
				minWidth="auto"
				required
				placeholder="09:00"
				menuPlaceholder="Type time and select"
				creatable
				closeOnSelect
				createOptionLabel={(query: string) => {
					const time = parseClockTime(query)
					return time ? `Set: ${formatDate(time, "HH:mm")} hrs` : "Invalid Time"
				}}
				onCreateNew={(query: string) => {
					const time = parseClockTime(query)
					return time ? formatDate(time, "HH:mm") : ""
				}}
			/>
		</Box>
	)
}

/**
 * The shared "send window" editor: an allowed local time-of-day range
 * (`send_window.from` / `send_window.to`, "HH:MM").
 *
 * What the backend does with a run computed outside the window depends on what
 * moving it would cost: a day-level checkpoint shifts to the nearest edge, an
 * hour or minute one keeps its exact time, and a repeat tick waits for the next
 * open.
 */
export function SendWindowField() {
	return (
		<Stack gap="2">
			<Inline gap="2" alignItems="center" flexWrap="wrap">
				<Text fontSize="sm" color="muted">
					Between
				</Text>
				<TimeSelectField name="send_window.from" />
				<Text fontSize="sm" color="muted">
					and
				</Text>
				<TimeSelectField name="send_window.to" />
				<Text fontSize="sm" color="muted">
					Hrs
				</Text>
			</Inline>
			<Stack gap="1">
				<Text color="muted" fontSize="sm">
					Only send within these local hours.
				</Text>
				<Box
					as="ul"
					listStyleType="disc"
					paddingLeft="4"
					color="muted"
					fontSize="sm"
				>
					<Box as="li">
						Checkpoints in days shift to the nearest edge of the window.
					</Box>
					<Box as="li">
						Checkpoints in hours or minutes keep their exact time — shifting
						them would change what they mean.
					</Box>
					<Box as="li">Repeats resume at the next window open.</Box>
				</Box>
			</Stack>
		</Stack>
	)
}
